import React from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { renderTripRoute } from '../trip/tripRoute';

const vehicleLabel = (s) => {
   const label = `${s.vehicleYear ?? ''} ${s.vehicleMake ?? ''} ${s.vehicleModel ?? ''}`.trim();
   return label || 'Unspecified vehicle';
};

const vehicleStatus = (v) => {
   if (!v.confirmed) {
      return { label: 'Needs confirmation', severity: 'warning' };
   }
   if (!v.pickedUpDateTime && !v.actualDepartDateTime) {
      return { label: 'Ready to pick up', severity: 'info' };
   }
   if (!v.actualDepartDateTime) {
      return { label: 'Ready to begin', severity: 'info' };
   }
   if (!v.turnAroundTime) {
      return { label: 'Start return', severity: 'info' };
   }
   if (!v.completed) {
      return { label: 'Return in progress', severity: 'success' };
   }
   return { label: 'Completed', severity: 'success' };
};

const formatMilestone = (dt) => {
   if (!dt) {
      return '';
   }
   const d = new Date(dt);
   return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
   });
};

const vehicleTimes = (v) => {
   const items = [];
   if (v.pickedUpDateTime) {
      items.push(`Picked up ${formatMilestone(v.pickedUpDateTime)}`);
   }
   if (v.actualDepartDateTime) {
      items.push(`Began ${formatMilestone(v.actualDepartDateTime)}`);
   }
   if (v.turnAroundTime) {
      items.push(`Turned around ${formatMilestone(v.turnAroundTime)}`);
   }
   if (v.completedDateTime) {
      items.push(`Completed ${formatMilestone(v.completedDateTime)}`);
   }
   if (items.length === 0) {
      return null;
   }
   return <div className="job-vehicle-times text-xs text-color-secondary">{items.join(' · ')}</div>;
};

export const MyTrip = ({ trip, userId, onOpen }) => {
   const userVehicles = (trip.TripVehicles || []).filter((v) => v.porterId == userId);

   const openVehicle = (vehicleId) => {
      if (onOpen) {
         onOpen(vehicleId);
      }
   };

   const firstActionVehicle = () => {
      return userVehicles.find((v) => !v.confirmed)
         || userVehicles.find((v) => !v.pickedUpDateTime && !v.actualDepartDateTime)
         || userVehicles.find((v) => !v.actualDepartDateTime)
         || userVehicles.find((v) => !v.turnAroundTime)
         || userVehicles.find((v) => !v.completed)
         || userVehicles[0];
   };

   const formatWindow = () => {
      const start = new Date(trip.requestedDeliveryDateTime);
      const startText = start.toLocaleDateString('en-US', {
         weekday: 'short',
         month: 'short',
         day: 'numeric',
         hour: 'numeric',
         minute: '2-digit',
         hour12: true
      });
      if (trip.requestedDeliveryDateTimeEnd) {
         const end = new Date(trip.requestedDeliveryDateTimeEnd);
         const endText = end.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
         });
         return `${startText} – ${endText}`;
      }
      return startText;
   };

   const headlineStatus = () => {
      if (userVehicles.some((v) => !v.confirmed)) {
         return vehicleStatus(userVehicles.find((v) => !v.confirmed));
      }
      if (userVehicles.some((v) => !v.pickedUpDateTime && !v.actualDepartDateTime)) {
         return vehicleStatus(userVehicles.find((v) => !v.pickedUpDateTime && !v.actualDepartDateTime));
      }
      if (userVehicles.some((v) => !v.actualDepartDateTime)) {
         return vehicleStatus(userVehicles.find((v) => !v.actualDepartDateTime));
      }
      if (userVehicles.some((v) => !v.turnAroundTime)) {
         return vehicleStatus(userVehicles.find((v) => !v.turnAroundTime));
      }
      if (userVehicles.some((v) => !v.completed)) {
         return vehicleStatus(userVehicles.find((v) => !v.completed));
      }
      return { label: 'Completed', severity: 'success' };
   };

   const status = headlineStatus();
   const dealershipName = trip.Dealership?.dealershipName;

   return (
      <div className="job-card job-card-clickable" onClick={() => openVehicle(firstActionVehicle()?.id)}>
         <div className="job-card-top">
            <div>
               <div className="job-card-when">Deliver by {formatWindow()}</div>
               <div className="job-card-route">{renderTripRoute(trip, userVehicles)}</div>
               <div className="job-card-meta">
                  {dealershipName ? `${dealershipName} · ` : ''}
                  {trip.estimatedDistance}
                  {trip.estimatedDuration ? ` · ${trip.estimatedDuration}` : ''}
               </div>
            </div>
            <Tag severity={status.severity} value={status.label}></Tag>
         </div>
         <div className="job-card-vehicles">
            <div className="job-card-section">Your vehicles</div>
            {userVehicles.map((v) => {
               const vs = vehicleStatus(v);
               return (
                  <div
                     key={v.id}
                     className="job-vehicle-row"
                     onClick={(e) => {
                        e.stopPropagation();
                        openVehicle(v.id);
                     }}
                  >
                     <div className="job-vehicle-info">
                        <span className="job-vehicle-name">{vehicleLabel(v)}</span>
                        <div className="job-card-route">{renderTripRoute(trip, [v])}</div>
                        {vehicleTimes(v)}
                     </div>
                     <Tag severity={vs.severity} value={vs.label}></Tag>
                  </div>
               );
            })}
         </div>
         <div className="text-right mt-2">
            <Button
               label="Open trip"
               icon="pi pi-arrow-right"
               size="small"
               onClick={(e) => {
                  e.stopPropagation();
                  openVehicle(firstActionVehicle()?.id);
               }}
            />
         </div>
      </div>
   );
};
